import { FC, useEffect, useMemo, useState } from 'react';
import { PD_RANGE_BY_FRAME_SIZE, PD_RANGE_BY_FRAME_SIZE_MONO } from 'constants/pdRange';
import PdInputOverlappingDropdown from './PdInputOverlappingDropdown';
import UIRadioList from './UIRadio';

export interface NewPDValue {
  type: 'bino' | 'mono';
  right?: string;
  left?: string;
  pd?: string;
  source?: string;
}

interface UICreatePDSelectProps {
  frameSize: 'XS' | 'S' | 'M' | 'L';
  value?: NewPDValue;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: NewPDValue) => void;
}

const UICreatePDSelect: FC<UICreatePDSelectProps> = ({
  value,
  onChange,
  frameSize = 'M'
}) => {
  const pdOptions = useMemo(() => {
    if (!value) return [];
    const pdList = [];
    const { min, max } = value.type === 'bino' ? PD_RANGE_BY_FRAME_SIZE[frameSize] : PD_RANGE_BY_FRAME_SIZE_MONO[frameSize];
    const minRange = min;
    const maxRange = max;

    for (let i = minRange; i <= maxRange; i += 0.5) {
      pdList.push(i);
    }

    return pdList;
  }, [frameSize, value]);

  const selectedPdType = useMemo(() => {
    return value?.type || 'bino';
  }, [value?.type]);

  useEffect(() => {
    if (value) return;

    onChange({
      type: 'bino',
      pd: '',
      left: '',
      right: ''
    });
  });

  const [selectActiveState, setSelectActiveState] = useState({
    binoPd: false,
    monoLeft: false,
    monoRight: false
  });

  const onPdTypeChanged = (type: 'bino' | 'mono') => {
    onChange({
      type,
      left: JSON.parse(localStorage.getItem('calculatedPd') || '{}')?.monoPD || '',
      right: JSON.parse(localStorage.getItem('calculatedPd') || '{}')?.monoPD || '',
      pd: JSON.parse(localStorage.getItem('calculatedPd') || '{}')?.pd || ''
    });
  };

  return (
    <div>
      <UIRadioList
        labelClassName="text-[14px]"
        value={value?.type}
        options={[
          {
            text: 'I Have 1 PD',
            value: 'bino',
          },
          {
            text: 'I Have 2 PDs',
            value: 'mono',
          }
        ]}
        onChange={(type) => onPdTypeChanged(type as any)}
      />
      {selectedPdType === 'bino' && (
        <div className="mt-[12px] md:w-[140px] w-[130px]">
          <PdInputOverlappingDropdown
            placeholder={selectedPdType}
            onChangePd={onChange}
            active={selectActiveState.binoPd}
            value={value && value.pd}
            pdValue={value}
            type={'pd'}
            updateActiveState={(value) =>
              setSelectActiveState({
                ...selectActiveState,
                binoPd: value
              })
            }>
            {pdOptions.map((pdOption) => (
              <div
                onClick={() => {
                  value &&
                    onChange({
                      ...value,
                      pd: String(pdOption)
                    });

                  setSelectActiveState({
                    binoPd: false,
                    monoLeft: false,
                    monoRight: false
                  });
                }}
                className="group border-b border-brand-grey500 text-brand-black25 transition-colors first:mt-[-12px] last:border-none sm:hover:text-brand-orange"
                key={pdOption}>
                <div className="cursor-pointer py-[6px]">{pdOption}</div>
              </div>
            ))}
          </PdInputOverlappingDropdown>
        </div>
      )}
      {selectedPdType === 'mono' && (
        <div className="mt-[12px] grid grid-cols-2 gap-[6px] sm:gap-[20px] md:w-[300px] w-[275px]">
          <PdInputOverlappingDropdown
            onChangePd={onChange}
            placeholder="monoR"
            type={'pd'}
            active={selectActiveState.monoRight}
            value={value && value.right}
            pdValue={value}
            updateActiveState={(value) =>
              setSelectActiveState({
                ...selectActiveState,
                monoRight: value
              })
            }>
            {pdOptions.map((pdOption) => (
              <div
                onClick={() => {
                  value &&
                    onChange({
                      ...value,
                      right: String(pdOption)
                    });

                  setSelectActiveState({
                    binoPd: false,
                    monoLeft: false,
                    monoRight: false
                  });
                }}
                className="group border-b border-brand-grey500 text-brand-black25 transition-colors first:mt-[-12px] last:border-none sm:hover:text-brand-orange"
                key={pdOption + '_right'}>
                <div className="cursor-pointer py-[6px]">{pdOption}</div>
              </div>
            ))}
          </PdInputOverlappingDropdown>
          <PdInputOverlappingDropdown
            placeholder="monoL"
            type={'pd'}
            onChangePd={onChange}
            active={selectActiveState.monoLeft}
            value={value && value.left}
            pdValue={value}
            updateActiveState={(value) =>
              setSelectActiveState({
                ...selectActiveState,
                monoLeft: value
              })
            }>
            {pdOptions.map((pdOption) => (
              <div
                onClick={() => {
                  value &&
                    onChange({
                      ...value,
                      left: String(pdOption)
                    });

                  setSelectActiveState({
                    binoPd: false,
                    monoLeft: false,
                    monoRight: false
                  });
                }}
                className="group border-b border-brand-grey500 text-brand-black25 transition-colors first:mt-[-12px] last:border-none sm:hover:text-brand-orange"
                key={pdOption + '_left'}>
                <div className="cursor-pointer py-[6px]">{pdOption}</div>
              </div>
            ))}
          </PdInputOverlappingDropdown>

        </div>
      )}
    </div>
  );
};

export default UICreatePDSelect;
